import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import XIcon from 'icons/X.icon';
import { Button, Loader } from 'ncoded-component-library';
import useScrollLock from 'hooks/useScrollLock';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useScrollToStart from 'hooks/useScrollToStart';

import './SideDrawer.styles.scss';
import './SideDrawer.styles.responsive.scss';

export type SideDrawerProps = {
  className?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  overlayClassName?: string;
  closeAction?: () => void;
  openAction?: () => void;
};

export type SideDrawerRef = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const SideDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  SideDrawerProps
> = (props, ref) => {
  const {
    children,
    className,
    isLoading,
    closeAction,
    openAction,
    overlayClassName,
  } = props;
  const [open, setOpen] = useState(false);
  const firstRun = useRef(true);

  const [contentRef, scrollToStart] = useScrollToStart();

  const { lockScroll, unlockScroll } = useScrollLock();

  const classes = classNames('side-drawer', { open }, className);
  const overlayClasses = classNames(
    'side-drawer__overlay',
    { open },
    overlayClassName,
  );

  useImperativeHandle(
    ref,
    () => ({
      open,
      setOpen,
    }),
    [open],
  );

  const onClose = useCallback(() => setOpen(false), []);

  useCloseOnEscape(onClose);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    if (open) {
      scrollToStart();
      lockScroll();
      openAction?.();
    } else {
      unlockScroll();
      closeAction?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  return (
    <>
      <div className={overlayClasses} onClick={onClose} />
      <div className={classes}>
        <div ref={contentRef}>{isLoading ? <Loader /> : children}</div>
        <Button
          className="close-drawer-button"
          icon={XIcon}
          onClick={onClose}
        />
      </div>
    </>
  );
};

export default forwardRef(SideDrawer);
